import React, { useCallback, useState } from "react";
import { withJsonFormsControlProps } from "@jsonforms/react";
import {
    ControlProps,
    rankWith,
    schemaMatches,
    RankedTester,
} from "@jsonforms/core";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Alert from "@mui/material/Alert";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import CircularProgress from "@mui/material/CircularProgress";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import ImageIcon from "@mui/icons-material/Image";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { useTranslation } from "react-i18next";
import { apiCallBlob } from "../../services/apiClient";

interface AttachmentItem {
    attachmentId: string;
    fileName: string;
    fileType: string;
}

const isPreviewable = (fileType: string) => fileType.startsWith("image/");

const getFileIcon = (fileType: string) => {
    if (fileType.startsWith("image/")) return <ImageIcon />;
    if (fileType === "application/pdf") return <PictureAsPdfIcon />;
    return <InsertDriveFileIcon />;
};

const AttachmentReadOnlyRendererComponent = ({
    data,
    schema,
    config,
}: ControlProps) => {
    const { t } = useTranslation();
    const [downloadError, setDownloadError] = useState<string | null>(null);
    const [previewItem, setPreviewItem] = useState<AttachmentItem | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [previewLoading, setPreviewLoading] = useState(false);

    const items: AttachmentItem[] = Array.isArray(data) ? data : [];

    const isPreview =
        (config as Record<string, unknown>)?.isEditorPreview === true;

    const fetchBlob = useCallback(async(item: AttachmentItem) => {
        setDownloadError(null);
        const response = await apiCallBlob(
            `FormAnswerAttachments/${encodeURIComponent(item.attachmentId)}/download`,
            "GET"
        );
        return response.data;
    }, []);

    const handleDownload = useCallback(
        async(item: AttachmentItem) => {
            try {
                const blob = await fetchBlob(item);
                const url = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.download = item.fileName;
                link.click();
                URL.revokeObjectURL(url);
            } catch {
                setDownloadError(t("reporting.formSummary.downloadFailed"));
            }
        },
        [fetchBlob, t]
    );

    const handlePreview = useCallback(
        async(item: AttachmentItem) => {
            if (!isPreviewable(item.fileType)) {
                void handleDownload(item);
                return;
            }
            setPreviewItem(item);
            setPreviewLoading(true);
            try {
                const blob = await fetchBlob(item);
                const url = URL.createObjectURL(blob);
                setPreviewUrl(url);
            } catch {
                setPreviewItem(null);
                setDownloadError(t("reporting.formSummary.downloadFailed"));
            } finally {
                setPreviewLoading(false);
            }
        },
        [fetchBlob, handleDownload, t]
    );

    const handleClosePreview = useCallback(() => {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setPreviewItem(null);
        setPreviewUrl(null);
    }, [previewUrl]);

    if (isPreview) {
        return (
            <Box sx={{ mb: 2 }}>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                    {schema.title}
                </Typography>
                <Alert severity="info" icon={<AttachFileIcon />} role="note">
                    {t("settings.formEditor.attachmentPreviewPlaceholder")}
                </Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ mb: 2 }}>
            <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                {schema.title}
            </Typography>
            {items.length > 0 ? (
                <List dense>
                    {items.map((item) => (
                        <ListItem
                            key={item.attachmentId}
                            data-testid={`attachment-item-${item.attachmentId}`}
                            disablePadding
                            secondaryAction={
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                    }}
                                >
                                    {isPreviewable(item.fileType) && (
                                        <IconButton
                                            size="small"
                                            color="primary"
                                            onClick={() =>
                                                void handlePreview(item)
                                            }
                                        >
                                            <VisibilityIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                    <IconButton
                                        edge="end"
                                        size="small"
                                        onClick={() =>
                                            void handleDownload(item)
                                        }
                                        title={t("common.download")}
                                        data-testid={`attachment-download-${item.attachmentId}`}
                                    >
                                        <DownloadIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            }
                        >
                            <ListItemIcon sx={{ minWidth: 36 }}>
                                {getFileIcon(item.fileType)}
                            </ListItemIcon>
                            <ListItemText
                                primary={item.fileName}
                                primaryTypographyProps={{
                                    variant: "body2",
                                    noWrap: true,
                                }}
                            />
                        </ListItem>
                    ))}
                </List>
            ) : (
                <Typography variant="body2" color="text.secondary">
                    -
                </Typography>
            )}
            {downloadError && (
                <Typography variant="caption" color="error">
                    {downloadError}
                </Typography>
            )}
            <Dialog
                open={previewItem !== null}
                onClose={handleClosePreview}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                    }}
                >
                    <Typography variant="h6" noWrap sx={{ flex: 1 }}>
                        {previewItem?.fileName}
                    </Typography>
                    <IconButton onClick={handleClosePreview} edge="end">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: 400,
                    }}
                >
                    {previewLoading && <CircularProgress />}
                    {previewUrl && previewItem && (
                        <img
                            src={previewUrl}
                            alt={previewItem.fileName}
                            style={{
                                maxWidth: "100%",
                                maxHeight: "80vh",
                                objectFit: "contain",
                            }}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export const attachmentRendererTester: RankedTester = rankWith(
    10,
    schemaMatches((schema) => schema.format === "attachment")
);

export const AttachmentReadOnlyRenderer = withJsonFormsControlProps(
    AttachmentReadOnlyRendererComponent
);
